import { PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { call, put, select, takeEvery, takeLeading } from 'redux-saga/effects';
// import L from 'i18n-react';
import { api, responseErrors } from 'services';
import { IStoreState } from 'redux/types';
import { ELinksName } from 'services/constants/list-links-sidebar';
import { EChannelsTabs, ETabsChannel, ETabsGroups } from 'services/constants/lists-table';
import { notificationContainer } from '../../../untils/notificationContainer';
import {
  addTopChannelsRequest,
  addTopChannelsSuccess,
  blockChannelsRequest,
  changeRecommendedRequest,
  changeRecommendedSuccess,
  changeShowAdsRequest,
  changeShowAdsSuccess,
  changeCoverEnabledRequest,
  changeCoverEnabledSuccess,
  changeVerifiedRequest,
  changeVerifiedSuccess,
  channelsInitState,
  channelsListRequest,
  channelsListSuccess,
  deleteChannelsRequest,
  deletePostRequest,
  deletePostSuccess,
  getAdminsChannelsRequest,
  getAdminsChannelsSuccess,
  getDetailsChannelRequest,
  getDetailsChannelSuccess,
  getMediaFilesRequest,
  getMediaFilesSuccess,
  getMediaSalesRequest,
  getMediaSalesSuccess,
  getMembersRequest,
  getMembersSuccess,
  getPostCommentsRequest,
  getPostCommentsSuccess,
  getPostRequest,
  getPostSuccess,
  getPostsRequest,
  getPostsSuccess,
  getStatisticRequest,
  getStatisticSuccess,
  getTopChannelsRequest,
  getTopChannelsSuccess,
  moveTopChannelsRequest,
  moveTopChannelsSuccess,
  postMediaFilesRequest,
  postMediaFilesSuccess,
  removeTopChannelsRequest,
  removeTopChannelsSuccess,
  topChannelsUpdateFinished,
} from './reducer';
import {
  IAdminsChannelsResponse,
  IAdminsChannelsSaga,
  IChannelsRequest,
  IChannelsResponse,
  ICommentsResponse,
  ICommentsSagaRequest,
  IDeactivateSagaRequest,
  IDeletePost,
  IDeleteSagaRequest,
  IDetailsChannel,
  IMediaFilesListRequest,
  IMediaFilesListResponse,
  IMediaFilesRequest,
  IMediaFilesResponse,
  IMediaSalesResponse,
  IMediaSalesSaga,
  IMembersResponse,
  IMembersSaga,
  IMoveTopRequest,
  IPostRequest,
  IPostResponse,
  IPostsResponse,
  IPostsSaga,
  IRecommendedRequest,
  IShowAdsRequest,
  IStatisticRequest,
  IStatisticResponse,
  ITopCannelRequest,
  IVerifiedREquest,
  ICoverEnabledRequest,
} from './types';

function* channelsListWorker(action: PayloadAction<IChannelsRequest>) {
  const { payload } = action;

  try {
    const response: { data: IChannelsResponse } = yield call(api.channels.channelsList, payload);
    yield put(channelsListSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getStatisticWorker(action: PayloadAction<IStatisticRequest>) {
  const { payload } = action;

  try {
    const response: IStatisticResponse = yield call(api.channels.getStatistic, payload);

    yield put(getStatisticSuccess(response));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getDetailsChannelWorker(action: PayloadAction<string>) {
  const { payload } = action;

  try {
    const response: { data: IDetailsChannel } = yield call(api.channels.getDetailsChanel, payload);
    yield put(getDetailsChannelSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* postVerifiedAccountWorker(action: PayloadAction<IVerifiedREquest>) {
  const { payload } = action;

  try {
    yield call(api.channels.postVerifiedAccountChanel, payload);
    yield put(changeVerifiedSuccess(payload));
    notificationContainer(
      payload.verify ? 'Account verification successful' : 'Verification is disabled',
      'success',
    );
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* changeRecommendedWorker(action: PayloadAction<IRecommendedRequest>) {
  const { payload } = action;

  try {
    yield call(api.channels.postChangeRecommended, payload);
    yield put(changeRecommendedSuccess(payload));
    notificationContainer(
      payload.recommend ? 'The channel is recommended' : 'The channel is not recommended',
      'success',
    );
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* blockChannelWorker(action: PayloadAction<IDeactivateSagaRequest>) {
  const { paramsApi, navigate, group } = action.payload;

  try {
    yield call(api.channels.blockChannel, paramsApi);
    // yield notificationContainer('User deleted successfully', 'success');
    yield navigate(
      group
        ? `${ELinksName.GROUPS}/${ETabsGroups.BLOCKED_GROUPS}`
        : `${ELinksName.CHANNELS}/${ETabsChannel.BLOCKED_CHANNELS}`,
    );
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(channelsInitState());
  }
}

function* deleteChannelWorker(action: PayloadAction<IDeleteSagaRequest>) {
  const { paramsApi, navigate, group } = action.payload;

  try {
    yield call(api.channels.deleteChannel, paramsApi);
    yield notificationContainer('User deleted successfully', 'success');
    yield navigate(
      group
        ? `${ELinksName.GROUPS}/${ETabsGroups.DELETE_GROUPS}`
        : `${ELinksName.CHANNELS}/${ETabsChannel.ALL_CHANNELS}`,
    );
  } catch (error) {
    if (isAxiosError(error)) {
      responseErrors(error);
    }
  } finally {
    yield put(channelsInitState());
  }
}

function* getAdminsWorker(action: PayloadAction<IAdminsChannelsSaga>) {
  const { payload } = action;

  try {
    const response: { data: IAdminsChannelsResponse } = yield call(api.channels.getAdmins, payload);
    yield put(getAdminsChannelsSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getMembersWorker(action: PayloadAction<IMembersSaga>) {
  const { payload } = action;

  try {
    const response: { data: IMembersResponse } = yield call(api.channels.getMembers, payload);
    yield put(getMembersSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getMediaSalesWorker(action: PayloadAction<IMediaSalesSaga>) {
  const { payload } = action;

  try {
    const response: { data: IMediaSalesResponse } = yield call(api.channels.getMediaSails, payload);
    yield put(getMediaSalesSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getPostsWorker(action: PayloadAction<IPostsSaga>) {
  const { payload } = action;

  try {
    const response: { data: IPostsResponse } = yield call(api.channels.getPosts, payload);
    yield put(getPostsSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getPostWorker(action: PayloadAction<IPostRequest>) {
  const { payload } = action;

  try {
    const response: { data: IPostResponse } = yield call(api.channels.getPost, payload);
    yield put(getPostSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getPostCommentsWorker(action: PayloadAction<ICommentsSagaRequest>) {
  const { payload } = action;

  try {
    const response: { data: ICommentsResponse } = yield call(api.channels.getPostComments, payload);
    yield put(getPostCommentsSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* deletePostWorker(action: PayloadAction<IDeletePost>) {
  const { payload, type, navigate } = action.payload;

  try {
    yield call(api.channels.deletePost, payload);
    yield type === 'removeAll'
      ? notificationContainer('Post deleted', 'success')
      : put(deletePostSuccess(payload));
    type === 'removeAll' &&
      navigate(
        `${ELinksName.CHANNEL_DETAIL_USER}/${payload.channel_id}/all-channels/${EChannelsTabs.POST}`,
      );
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* postMediaFilesWorker(action: PayloadAction<IMediaFilesListRequest>) {
  const { payload } = action;

  try {
    const response: { data: IMediaFilesListResponse } = yield call(
      api.channels.postMediaFiles,
      payload,
    );
    yield put(postMediaFilesSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getMediaFilesWorker(action: PayloadAction<IMediaFilesRequest>) {
  const { payload } = action;

  try {
    const response: { data: IMediaFilesResponse } = yield call(api.channels.getMediaFile, payload);
    yield put(getMediaFilesSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* getTopChannelsWorker() {
  try {
    const response: { data: IChannelsResponse } = yield call(api.channels.getTopChannels);

    yield put(getTopChannelsSuccess(response.data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

// Refresh only after the mutation has completed, using the active filters/page.
function* refreshRecommendedChannelsWorker(
  successAction: (payload: IChannelsResponse) => PayloadAction<IChannelsResponse>,
  refreshList: boolean,
) {
  const response: { data: IChannelsResponse } = yield call(api.channels.getTopChannels);
  yield put(successAction(response.data));

  if (refreshList) {
    const params: IChannelsRequest | null = yield select(
      (state: IStoreState) => state.channels.channelsListParams,
    );
    if (params) {
      const list: { data: IChannelsResponse } = yield call(api.channels.channelsList, params);
      const currentParams: IChannelsRequest | null = yield select(
        (state: IStoreState) => state.channels.channelsListParams,
      );
      // A tab/filter change during the request owns the newer list.
      if (currentParams === params) {
        yield put(channelsListSuccess(list.data));
      }
    }
  }
}

function* updateTopChannelsWorker(
  action: PayloadAction<ITopCannelRequest | IMoveTopRequest>,
) {
  try {
    switch (action.type) {
      case addTopChannelsRequest.type:
        yield call(api.channels.addTopChannel, action.payload as ITopCannelRequest);
        yield call(refreshRecommendedChannelsWorker, addTopChannelsSuccess, true);
        break;
      case removeTopChannelsRequest.type:
        yield call(api.channels.removeTopChannel, action.payload as ITopCannelRequest);
        yield call(refreshRecommendedChannelsWorker, removeTopChannelsSuccess, true);
        break;
      case moveTopChannelsRequest.type:
        yield call(api.channels.moveTopChannel, action.payload as IMoveTopRequest);
        yield call(refreshRecommendedChannelsWorker, moveTopChannelsSuccess, false);
        break;
    }
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(topChannelsUpdateFinished());
  }
}

function* changeShowAdsWorker(action: PayloadAction<IShowAdsRequest>) {
  const { payload } = action;

  try {
    yield call(api.channels.postShowAds, payload);
    yield put(changeShowAdsSuccess(payload));
    notificationContainer(
      payload.show
        ? 'Video AD is enabled for this channel'
        : 'Video AD is disabled for this channel',
      'success',
    );
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
  } finally {
    yield put(channelsInitState());
  }
}

function* changeCoverEnabledWorker(action: PayloadAction<ICoverEnabledRequest>) {
  const { channel_id, cover_enabled, onSuccess, onError } = action.payload;
  const request = { channel_id, cover_enabled };

  try {
    yield call(api.channels.postCoverEnabled, request);
    yield put(changeCoverEnabledSuccess(request));
    onSuccess?.();
    notificationContainer(
      cover_enabled ? 'Media preview is enabled' : 'Media preview is disabled',
      'success',
    );
  } catch (error) {
    onError?.();
    if (isAxiosError(error)) responseErrors(error);
  }
}

export function* channelsSaga() {
  yield takeEvery(channelsListRequest.type, channelsListWorker);
  yield takeEvery(getStatisticRequest.type, getStatisticWorker);
  yield takeEvery(getDetailsChannelRequest.type, getDetailsChannelWorker);
  yield takeEvery(changeVerifiedRequest.type, postVerifiedAccountWorker);
  yield takeEvery(changeRecommendedRequest.type, changeRecommendedWorker);
  yield takeEvery(blockChannelsRequest.type, blockChannelWorker);
  yield takeEvery(deleteChannelsRequest.type, deleteChannelWorker);
  yield takeEvery(getAdminsChannelsRequest.type, getAdminsWorker);
  yield takeEvery(getMembersRequest.type, getMembersWorker);
  yield takeEvery(getMediaSalesRequest.type, getMediaSalesWorker);
  yield takeEvery(getPostsRequest.type, getPostsWorker);
  yield takeEvery(getPostRequest.type, getPostWorker);
  yield takeEvery(getPostCommentsRequest.type, getPostCommentsWorker);
  yield takeEvery(deletePostRequest.type, deletePostWorker);
  yield takeEvery(postMediaFilesRequest.type, postMediaFilesWorker);
  yield takeEvery(getMediaFilesRequest.type, getMediaFilesWorker);
  yield takeEvery(getTopChannelsRequest.type, getTopChannelsWorker);
  yield takeLeading(
    [addTopChannelsRequest.type, removeTopChannelsRequest.type, moveTopChannelsRequest.type],
    updateTopChannelsWorker,
  );
  yield takeEvery(changeShowAdsRequest.type, changeShowAdsWorker);
  yield takeEvery(changeCoverEnabledRequest.type, changeCoverEnabledWorker);
}
